import { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react'
import {
  Routes, Route, NavLink, Link, Navigate, useLocation, useNavigate, useSearchParams,
} from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useMe, useLookups, useApi } from './lib/api'
import {
  signOut,
  signOutForIdling,
  canSignOut,
  mayLoseUnsavedWork,
  NO_SIGN_OUT_HINT,
  SIGN_IN_AGAIN_HINT,
} from './lib/session'
import { useLeaving } from './lib/leaving'
import { useIdleTimeout, IDLE_MS } from './lib/idle'
import { donorContext } from './lib/donor'
import { ViewModeProvider, useCanBrowse } from './lib/viewmode'
import { PartnerTermProvider, useRelabel } from './lib/terms'
import { ThemeCycleButton } from './lib/theme'
import { Loading, Notice, Badge, ErrorState, Modal, Spinner } from './components/ui'
import ErrorBoundary from './components/ErrorBoundary'
import { Icon } from './components/Icon'
import { SUITE, HERE } from './lib/suite'

import Dashboard from './pages/Dashboard'
import UpdatesOverview, { SEARCH_INPUT_ID } from './pages/UpdatesOverview'
import AnnualReports from './pages/AnnualReports'
import NewsletterIssues from './pages/NewsletterIssues'
import NewsletterList from './pages/NewsletterList'
import NewsletterDetail from './pages/NewsletterDetail'
import NewsletterForm from './pages/NewsletterForm'
import PrayerRequests from './pages/PrayerRequests'
import HighlightedNeeds from './pages/HighlightedNeeds'
import MediaLibrary from './pages/MediaLibrary'
import PartnerList from './pages/PartnerList'
import { countOfKind } from '../shared/fundTypes.js'
import PartnerDetail from './pages/PartnerDetail'
import PartnerForm from './pages/PartnerForm'
import CountryList from './pages/CountryList'
import CountryDetail from './pages/CountryDetail'
import ReportsIndex from './pages/ReportsIndex'
import DonorsByPartner from './pages/DonorsByPartner'
import EmailPartnerUnits from './pages/EmailPartnerUnits'
import EmailTemplates from './pages/EmailTemplates'
import CommunicationLog from './pages/CommunicationLog'
import NewslettersByPartner from './pages/NewslettersByPartner'
import CoverageReport from './pages/CoverageReport'
import ActivityReport from './pages/ActivityReport'
import CountryComparison from './pages/CountryComparison'
import TextAnalytics from './pages/TextAnalytics'
import AiUsageReport from './pages/AiUsageReport'
import TimelineReport from './pages/TimelineReport'
import DataQuality from './pages/DataQuality'
import PromptsManager from './pages/PromptsManager'
import AiStudio from './pages/AiStudio'
import SettingsAppearance from './pages/SettingsAppearance'
import SettingsLlm from './pages/SettingsLlm'
import SettingsUsers from './pages/SettingsUsers'
import SettingsDonors from './pages/SettingsDonors'
import DonorSignIn from './pages/DonorSignIn'
import SettingsAnnualReports from './pages/SettingsAnnualReports'
import SettingsNewsletterIssues from './pages/SettingsNewsletterIssues'
import SettingsPhotoImport from './pages/SettingsPhotoImport'
import SettingsPartnerImport from './pages/SettingsPartnerImport'
import SettingsDonorImport from './pages/SettingsDonorImport'
import Donors from './pages/Donors'
import AuditLog from './pages/AuditLog'
import ErrorLog from './pages/ErrorLog'
import Privacy from './pages/Privacy'

/*
 * `viewer: true` marks an item as part of the view a read-only account is given — the
 * five pages in migration 0012, and no others. Everything unmarked is left out of that
 * sidebar entirely rather than shown and refused: the Worker turns those routes down,
 * so a link to one is a promise the app cannot keep.
 */
/**
 * The one link that sits above the groups, in nobody's group.
 *
 * A dashboard is not a category of page, it is the page you land on, and folding it into
 * an accordion would have made the first thing anybody sees the thing they have to open a
 * group to reach. It is also what the brand lockup opens, so this entry is the second way
 * in rather than the only one — the same arrangement Updates Overview used to have.
 */
const TOP = { to: '/dashboard', icon: 'home', label: 'Home', viewer: true }

/**
 * The sidebar's icons: Material's filled set, drawn inline at 24 units. One per group
 * heading and one for Home — the links inside a group carry none, so the column reads
 * as a short list of places with their contents indented under whichever is open.
 *
 * Paths rather than the emoji the links used to carry: an emoji is drawn by the
 * platform in its own colours, and a sidebar where every row is a different colour is
 * one the eye cannot run down.
 */
const NavIcon = ({ name, className = 'nav-icon' }) => <Icon name={name} size={20} className={className} />

/* Where the old landing page went when `/` became a redirect. Named, because three
   places have to agree about it: the nav entry, the redirect above, and the `/` key
   below, which sends the cursor to the search box that lives on it. */
const OVERVIEW = '/overview'

/**
 * What `/` is now: a door to the dashboard rather than a page of its own.
 *
 * The dashboard is what the sidebar opens with and what the brand lockup opens, so the
 * bare domain landing somewhere else was the one place the app disagreed with itself —
 * and `/` is where Cloudflare Access returns somebody after a login.
 *
 * `?q=` is carried across instead of dropped. Search was reached at `/?q=…` for as long
 * as the overview was the landing page, so those links are in bookmarks and in the smoke
 * test; they go to the page the box is on now rather than silently losing the search.
 *
 * `replace`, so Back from the dashboard goes wherever the reader came from rather than to
 * a redirect that would bounce them straight forward again.
 */
function Home() {
  const [params] = useSearchParams()
  const q = params.get('q')
  return <Navigate to={q ? `${OVERVIEW}?q=${encodeURIComponent(q)}` : TOP.to} replace />
}

const NAV = [
  {
    label: 'Overview',
    icon: 'overview',
    /* Search had a link of its own here until its box moved to the top of the
       dashboard. One entry now goes to both. */
    items: [
      { to: OVERVIEW, label: 'Updates Overview', viewer: true },

      /* Part of a view-only account's view, and a donor's, which almost nothing else
         outside the five pages of migration 0012 is. An annual report is the document
         this organisation publishes about itself, and those accounts are who it was
         written for — they are served the edition with the faces covered. */
      { to: '/annual-reports', label: 'Annual Reports', count: 'annualReports', viewer: true },
      /* Beside the reports and for the same reason: the printed newsletter is the other
         document the organisation publishes about itself, and a supporter is who it was
         written for. `/newsletter-issues` rather than `/newsletters`, which has meant a
         partner unit's update since migration 0001 and is the sidebar's "Updates" below.
         The two share a word in English and nothing else. */
      { to: '/newsletter-issues', label: 'Newsletters', count: 'newsletterIssues', viewer: true },
      /* A reader's own shortlist of partner units. Built for the donor view — following
         a handful of units is the thing a supporter comes back for — and given to staff
         too, because the page is the directory with one filter on it and there is no
         reason the same shortlist would not be useful to somebody who works here. */
      /* The starred prayer requests of the last thirty days — the same page the
         dashboard's third door opens, and its own route rather than a query string
         because the thirty days move and a `to` written here would not. */
      /* "Current Prayer Needs", not "Highlighted Needs": the Content group's "Highlighted
         Updates" is every flagged update, and two pages a word apart read as one page
         listed twice. This one is the prayer side of it, and only what is current. */
      { to: '/prayer-needs', label: 'Current Prayer Needs', viewer: true },
      { to: '/favorites', label: 'Favorites', count: 'favorites', viewer: true },
    ],
  },
  {
    label: 'Content',
    icon: 'content',
    items: [
      { to: '/newsletters', label: 'Updates', count: 'newsletters', viewer: true },
      { to: '/prayer-requests', label: 'Prayer Requests', count: 'prayerRequests', viewer: true },
      { to: '/highlighted-needs', label: 'Highlighted Updates', count: 'highlighted', viewer: true },
      { to: '/media', label: 'Media Library', count: 'media' },
    ],
  },
  {
    label: 'Register',
    icon: 'register',
    items: [
      { to: '/partners', label: 'Partner Units', count: 'partners', viewer: true },
      /* The other two kinds of thing the S4CA registry has always held, given pages of
         their own now that the export says which is which — see migration 0031. All three
         are the same page with one filter pinned (PartnerList), so a training school gets
         the directory's cards, filters, sort, CSV and favourites for nothing.

         Beside Partner Units rather than under Content, because that is what they are: a
         training school is somebody the archive holds a record of and receives updates
         from, not a kind of document. `viewer: true` for the same reason the units carry
         it — a supporter following a project should be able to find it. */
      { to: '/training-schools', label: 'Training Schools', count: 'trainingSchools', viewer: true },
      { to: '/projects', label: 'Projects', count: 'projects', viewer: true },
      { to: '/countries', label: 'Country / Category', count: 'countries' },
    ],
  },
  {
    label: 'Reports',
    icon: 'reports',
    /* This group and the three below it carried a `shut: true` mark, because between
       them they are two thirds of the sidebar and all of them are somewhere you go now
       and then rather than all day. The accordion does that job for every group now —
       see GROUP_KEY — so the mark is gone rather than sitting there meaning nothing. */
    items: [
      { to: '/reports/newsletters-by-partner', label: 'Updates by Partner Unit' },
      { to: '/reports/coverage', label: 'Coverage & Gaps' },
      { to: '/reports/activity', label: 'Activity over Time' },
      { to: '/reports/timeline', label: 'Timeline' },
      { to: '/reports/countries', label: 'Country Comparison' },
      { to: '/reports/text', label: 'Text Analytics' },
      { to: '/data-quality', label: 'Data Quality' },
      /* `end` so the index only lights up on /reports itself — every report below
         it is a child path, and each has its own link in this group. */
      { to: '/reports', label: 'All Reports', end: true },
    ],
  },
  /* Writing to partner units, which is the one thing this application does that leaves it.
     Its own group because it is neither reading the archive nor configuring it, and every
     page in it is `manageSettings` — it reads the addresses the cards deliberately do not
     show, and it sends mail in the organisation's name. */
  {
    label: 'Communication',
    icon: 'communication',
    items: [
      { to: '/communication/email', label: 'Email Partner Units', cap: 'manageSettings' },
      { to: '/communication/templates', label: 'Email Templates', cap: 'manageSettings' },
      /* Third, and not one of the two that were asked for — but "keep records of all
         communication" has to be readable somewhere, and a log nobody can open is not a
         record. */
      { to: '/communication/log', label: 'Communication Log', count: 'communications', cap: 'manageSettings' },
    ],
  },
  {
    /* "AI Settings" rather than "AI": every other group here is named for the kind of
       page inside it, and a two-letter heading beside "Reports" and "Register" read as
       a category the others were exceptions to. */
    label: 'AI Settings',
    icon: 'ai',
    items: [
      { to: '/ai/prompts', label: 'Prompts' },
      { to: '/ai/studio', label: 'AI Studio' },
      { to: '/reports/ai-usage', label: 'AI Usage' },
      /* The models, the keys they are reached with, and which prompt each field runs.
         It was a Settings page called "AI & Models", which put the machinery in one
         group and the machinery's own configuration in another; the pages that talk
         about it now say "AI Settings → AI Models". Last in the group because it is the one
         page here you set up rather than use. */
      { to: '/settings/llm', label: 'AI Models', cap: 'manageSettings' },
    ],
  },
  /* Filling the archive from a file. Every page here is `manageSettings`, which is
     owners and administrators, and every one of them writes over many records at once
     from something somebody chose off a disk. */
  {
    label: 'Imports',
    icon: 'imports',
    items: [
      /* Owners and administrators: `manageSettings` is exactly those two roles. The
         page writes over the photograph on every record it matches, which is not
         something an editor's upload permission was ever meant to cover.

         "Import Pictures" rather than "Import Partner Pictures": one folder can carry
         partner units, training schools and projects together, because the code in a
         filename is matched against the whole registry and never cared which kind it
         found. */
      { to: '/settings/photo-import', label: 'Import Pictures', cap: 'manageSettings' },
      /* Shelving a report writes a file every donor can open, so it sits behind the same
         permission as the other two imports rather than behind `uploadMedia` alone. */
      { to: '/settings/annual-reports', label: 'Import Annual Reports', cap: 'manageSettings' },
      /* Directly under it: the same page for the other shelf, behind the same permission
         and asking the same question about whether the faces in the file are covered. */
      { to: '/settings/newsletter-issues', label: 'Import Newsletters', cap: 'manageSettings' },
      /* The other bulk import, and the same reasoning: it rewrites every field a registry
         export carries on every record it names, and creates the ones the archive has not
         got.

         "Import Units" rather than "Import Partner Units": one file carries all three
         kinds now, and a tab naming only the first would leave somebody hunting for where
         training schools and projects are loaded from. The route keeps its old path —
         renaming it would break every link and bookmark to it for a word. */
      { to: '/settings/partner-import', label: 'Import Units', cap: 'manageSettings' },
      /* Beneath the registry import, because it is the other file the office feeds the
         archive from, and it reads partner units the registry import created. */
      { to: '/settings/donor-import', label: 'Import Donors', cap: 'manageSettings' },
    ],
  },
  /* The people, by whichever door: who may open the archive, and who gives to it.
     A group of its own rather than the tail of the imports, because it answers a
     different question — and to two different roles. The two access pages are
     `manageUsers`, which is owners alone; the register below them is `manageSettings`,
     owners and administrators. So an administrator sees this group holding the register
     and nothing else, which is the whole of what they may see of it. */
  {
    label: 'Users & Donors',
    icon: 'users',
    items: [
      { to: '/settings/users', label: 'Users & Access', count: 'users', cap: 'manageUsers' },
      /* Directly under it, because the two are the same page for the two doors: that
         one is who may come through Cloudflare Zero Trust and what they may do here,
         this one is who has a password for the donor address. Same permission, since
         both answer the same question — who can see the archive. */
      { to: '/settings/donors', label: 'Donor Accounts', count: 'donors', cap: 'manageUsers' },
      /* Last, and one step out from the two above it: those are accounts — who holds a
         password — and this is the giving register, which is everybody the report knows
         about whether or not they can sign in. Its own capability for that reason. */
      { to: '/donors', label: 'Donor Register', count: 'donorRegistry', cap: 'manageSettings' },
      /* With the register it reads rather than among the reports, so every donor page is
         in this one group. Still a card on the Reports page, which is where somebody
         browsing reports will look for it. Its cap is the register's. */
      { to: '/reports/donors-by-partner', label: 'Donors by Partner Unit', cap: 'manageSettings' },
    ],
  },
  /* The two records of what the archive did, kept together and away from the pages
     that do it: one is what people did, the other what failed. Both name staff and
     donors by address, and the error log keeps IPs and locations, so both are
     `manageSettings` — owners and administrators — and the API refuses them to editors
     on the same terms. */
  {
    label: 'Audits & Logs',
    icon: 'logs',
    items: [
      { to: '/settings/audit', label: 'Audit Log', cap: 'manageSettings' },
      { to: '/settings/errors', label: 'Error Log', cap: 'manageSettings' },
    ],
  },
]

/**
 * Settings that live in the account menu and nowhere else — the top right corner is
 * the whole of the sidebar's old Settings group that was not about the archive.
 *
 * The rule, and the reason this is a list of its own rather than another NAV group:
 * anything in NAV above must not appear here. The nav is where you go for the
 * archive's own pages, the person button is for the few that are about you, and a
 * page in both is a page you have to look for twice.
 */
const ACCOUNT_SETTINGS = [
  { to: '/settings/appearance', icon: 'contrast', label: 'Appearance', viewer: true },
]

/**
 * Which group you left open. One label rather than a list of the shut ones, because
 * the sidebar is an accordion: opening a group closes the others, and so does landing
 * on a page inside one, so there is only ever one answer to keep.
 *
 * A new key rather than the old `s4n.nav.groups`, which held the inverse — an array of
 * folded labels. A browser that still has that one falls back to the default here
 * instead of having it read as something it is not.
 *
 * The labels above are the keys, so renaming a group forgets which one was open; it is
 * remembered again the next time one is clicked. Cheap enough that a stable id beside
 * every label would cost more to carry than the preference is worth.
 */
const GROUP_KEY = 's4n.nav.group'

/**
 * What to call the archive before the server has said.
 *
 * Only the screen shown when /api/me could not be reached uses this — everywhere else
 * the name has already arrived with the account. It is a last resort rather than a
 * default: an installation that has been renamed should never flash this on the way in.
 */
const FALLBACK_ORG = 's4narchive'

/** Does this nav item light up for the path we are on? Mirrors NavLink's own rule. */
const owns = (item, path) =>
  item.end ? path === item.to : path === item.to || path.startsWith(`${item.to}/`)

/** The group the current page lives in, if any. */
const groupFor = (path) =>
  NAV.find((g) => g.items.some((i) => owns(i, path)))?.label

/**
 * Which group is open on the first render.
 *
 * The dashboard comes first, and it is the one page that wants the sidebar shut. It
 * belongs to no group, so there is nothing down there to point at, and it is the page
 * everybody lands on — a column of open links under a page that is itself the way in to
 * all of them is noise on the one screen that should read as a starting point.
 *
 * Then the page you are on: a sidebar that cannot show you where you are is not worth
 * the room it saves, and it is also the only one of these answers that cannot be stale.
 * Then the group you last clicked open, which is what carries any other page belonging
 * to no group — the privacy policy, or a screen the router did not expect. Then the
 * first group, so the sidebar is never wholly shut anywhere else on the way in.
 *
 * A stored label that no longer matches a group is ignored rather than kept, which is
 * what makes renaming a group safe.
 */
const readOpenGroup = (path) => {
  if (owns(TOP, path)) return null
  const here = groupFor(path)
  if (here) return here
  try {
    const saved = localStorage.getItem(GROUP_KEY)
    if (NAV.some((g) => g.label === saved)) return saved
  } catch {}
  return NAV[0].label
}

function Nav({ open, onClose, caps, counts, orgName }) {
  // Three of the labels in NAV name a partner unit; the group headings do not, but they
  // cost nothing to pass through and the next one might. See lib/terms.jsx.
  const t = useRelabel()
  const canBrowse = useCanBrowse()
  const { pathname } = useLocation()
  const [openGroup, setOpenGroup] = useState(() => readOpenGroup(pathname))

  // Picking a page moves the open group to the one that page is in, and the rest close
  // with it. Adjusted while rendering rather than in an effect, so the group is never
  // painted shut for a frame with the active page inside it.
  //
  // The dashboard is the exception, and it shuts everything. It is the page the brand
  // lockup and the first entry both open, so it is where a reader starts, and a sidebar
  // at rest is what a starting point should look like — the groups are still one click
  // from open and their headings still say what is in them.
  //
  // Any other path in no group — the privacy policy — leaves the accordion where it was
  // rather than shutting it: nothing over there is the page you are on, so there is
  // nothing for it to point at, and closing the group you were reading from would only
  // cost you the way back.
  const [seen, setSeen] = useState(pathname)
  if (seen !== pathname) {
    setSeen(pathname)
    if (owns(TOP, pathname)) {
      // State only, never the stored preference. A group is remembered because somebody
      // clicked it open; arriving somewhere is not a decision about the next visit, and
      // the one this costs is a group deliberately opened *while on* the dashboard, which
      // a reload then shuts. One click back, against every landing being tidy.
      if (openGroup !== null) setOpenGroup(null)
    } else {
      const arrived = groupFor(pathname)
      if (arrived && arrived !== openGroup) setOpenGroup(arrived)
    }
  }

  // One open group, so opening one is the same action as closing the others. Clicking
  // the open one shuts it and leaves the sidebar with nothing but its headings, which
  // is a legitimate thing to want from a list this long.
  //
  // Only a click is remembered. A group opened by arriving somewhere is open for as
  // long as you are there, which is a different thing from one you chose.
  const toggleGroup = (label) => {
    const next = openGroup === label ? null : label
    setOpenGroup(next)
    try {
      if (next) localStorage.setItem(GROUP_KEY, next)
      else localStorage.removeItem(GROUP_KEY)
    } catch {}
  }

  return (
    <nav id="main-nav" className={`nav${open ? ' open' : ''}`} aria-label="Main navigation">
      {/* The whole lockup is the link, icon and wordmark together, which is where a
          reader expects to be able to click to get home. The Home link below is
          still the one that lights up for the route — this is a second way in, not a
          second place to look for where you are. */}
      <Link className="nav-brand" to={TOP.to} aria-label={`${orgName} — go to Home`}>
        {/* Decorative: the title beside it already names the app. A div rather than an
            img because the colour behind the mark comes from the palette — see
            .nav-logo, which layers the mark over --brand. */}
        <div className="nav-logo" aria-hidden="true" />
        <div className="nav-brand-text">
          <div className="nav-brand-title">{orgName}</div>
        </div>
      </Link>
      <div className="nav-scroll">
        {/* Above the groups and outside them, so the caret-and-fold pattern of the rest
            of the sidebar does not imply this one has anything inside it. */}
        <NavLink
          to={TOP.to}
          className={({ isActive }) => `nav-link nav-link-top${isActive ? ' active' : ''}`}
          onClick={onClose}
        >
          <NavIcon name={TOP.icon} />
          <span className="truncate">{t(TOP.label)}</span>
        </NavLink>
        {NAV.map((group) => {
          const items = group.items.filter(
            (i) => (canBrowse || i.viewer) && (!i.cap || caps?.[i.cap])
          )
          if (!items.length) return null
          const folded = openGroup !== group.label
          const listId = `nav-group-${group.label.toLowerCase().replace(/\s+/g, '-')}`
          return (
            <div className="nav-group" key={group.label}>
              {/* The heading is the control, rather than a separate handle beside it:
                  every group folds, so a handle would be a second column of them down
                  the whole sidebar. */}
              <button
                type="button"
                className="nav-group-label"
                onClick={() => toggleGroup(group.label)}
                aria-expanded={!folded}
                aria-controls={listId}
              >
                <NavIcon name={group.icon} />
                <span className="truncate">{t(group.label)}</span>
                {/* Ranged right, pointing down over a shut group and up over an open
                    one — the way round Bloomerang's sidebar has it, which is the look
                    this column was set to. */}
                <NavIcon name="chevron" className="nav-group-caret" />
              </button>
              {/* Indented under a rule, with no icons of their own: the heading's icon
                  says what they are, and the rule says whose they are. */}
              <div id={listId} className="nav-group-items" hidden={folded}>
                {items.map((i) => (
                  <NavLink
                    key={i.to}
                    to={i.to}
                    end={i.end}
                    className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
                    onClick={onClose}
                  >
                    <span className="truncate">{t(i.label)}</span>
                    {i.count && counts?.[i.count]?.value !== undefined && (
                      <span className="nav-link-count" title={t(counts[i.count].title)}>
                        {counts[i.count].value.toLocaleString()}
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </nav>
  )
}

/**
 * Keeps a nav badge honest about what the page is showing. A section's badge counts
 * the whole collection by default; while a list page is open it publishes the total
 * its own filters produced, so the sidebar and the page can never disagree. The
 * override is dropped when the page unmounts, and the default count comes back.
 */
const NavCountCtx = createContext(null)

export function useNavCount(key, value) {
  const setCounts = useContext(NavCountCtx)
  useEffect(() => {
    if (!setCounts) return
    setCounts((prev) => (key in prev && prev[key] === value ? prev : { ...prev, [key]: value }))
    return () =>
      setCounts((prev) => {
        if (!(key in prev)) return prev
        const next = { ...prev }
        delete next[key]
        return next
      })
  }, [setCounts, key, value])
}

/**
 * Identity and theme controls. Rendered inside each page's header rather than as
 * a fixed overlay, so they can never sit on top of a page's own action buttons.
 */
const ShellCtx = createContext(null)

/**
 * The ninety-minute clock, mounted inside the shell so that it runs on every page and only
 * for somebody who is actually signed in.
 *
 * A component rather than a hook call in App, because `user` is not known until after the
 * early returns above it and a hook cannot be called after those. Renders nothing.
 *
 * `canSignOut` is the same test the button uses: locally there is no Access in front of
 * the Worker and no session to end, so the clock would only navigate to a 404 — and a
 * developer locked out of the dev server every ninety minutes would delete this file.
 */
function IdleWatch({ user }) {
  const leave = useCallback(() => signOutForIdling(user), [user])
  useIdleTimeout({ enabled: canSignOut(user), onIdle: leave })
  return null
}

/**
 * What stands in for the archive while it is being left.
 *
 * The reason this exists is a flash of red. Every way out of here is a whole-page
 * navigation, and none of them are instant: the request that discovered the session was
 * gone had already rejected, react-query had already put it in its error state, and the
 * page still drew the card for it — "Signed out after 90 minutes without use" in danger
 * colours, over a fully drawn dashboard, gone again before it could be read. It was true,
 * and it looked exactly like a fault, which is how it was reported.
 *
 * So the moment anything commits to leaving, this takes the screen instead. It says the
 * same thing in the tone of a thing working as intended, and it says it for as long as
 * the navigation takes rather than for one frame.
 *
 * The link is not decoration. It is the only way off this screen if the navigation itself
 * fails — the network dropping between the decision and the login page would otherwise
 * leave somebody watching a spinner with nothing to press.
 */
const LEAVING_COPY = {
  idle: {
    title: 'Signing you out',
    body:
      `This window has been idle for ${Math.round(IDLE_MS / 60_000)} minutes, so the archive ` +
      'has closed the session. Taking you back to Cloudflare Access to sign in again…',
  },
  expired: {
    title: 'Signing you in again',
    body:
      'Your Cloudflare Access session has run out. Taking you back to the sign-in page, ' +
      'which will return you to the page you were on…',
  },
  signout: {
    title: 'Signing you out',
    body: 'Ending your session and leaving the archive…',
  },
}

function LeavingScreen({ reason, to }) {
  const copy = LEAVING_COPY[reason] ?? LEAVING_COPY.signout
  return (
    <div className="leaving" role="status" aria-live="polite">
      <div className="leaving-card">
        <div className="leaving-title">{copy.title}</div>
        <p className="leaving-body">{copy.body}</p>
        <Spinner label="One moment" />
        <a className="leaving-link small" href={to}>
          Continue to the sign-in page
        </a>
      </div>
    </div>
  )
}

/**
 * Folds the navigation away. It sits at the far left of the header, directly
 * above the menu it controls — on a wide screen the sidebar collapses in place,
 * on a narrow one the same button slides the drawer in and out.
 */
function NavToggle() {
  const shell = useContext(ShellCtx)
  if (!shell) return null
  const { navVisible, toggleNav } = shell
  return (
    <button
      className="btn btn-sm btn-icon nav-toggle"
      onClick={toggleNav}
      aria-label={navVisible ? 'Hide the navigation menu' : 'Show the navigation menu'}
      aria-expanded={navVisible}
      aria-controls="main-nav"
      title={navVisible ? 'Hide the menu' : 'Show the menu'}
    >
      {/* The same two icons as InfoCodes' menu button — Material's "menu open" while the
          menu is showing, its three bars while it is folded away — so the button says
          which way it will go. Inlined from Material Symbols (Apache 2.0) rather than
          pulled in with a package, this being the only one of them the app uses. */}
      <svg
        className="nav-toggle-bars"
        viewBox="0 0 24 24"
        width="20"
        height="20"
        aria-hidden="true"
        focusable="false"
        fill="currentColor"
      >
        <path
          d={
            navVisible
              ? 'M3 18h13v-2H3zm0-5h10v-2H3zm0-7v2h13V6zm18 9.59L17.42 12 21 8.41 19.59 7l-5 5 5 5z'
              : 'M3 18h18v-2H3zm0-5h18v-2H3zm0-7v2h18V6z'
          }
        />
      </svg>
    </button>
  )
}


/**
 * The account menu: the person in the top right corner of every page.
 *
 * One button, and behind it the three things that are about *you* rather than about
 * the page you are on — who you are signed in as, the settings that are yours to set,
 * and the way out. Each of the three was somewhere else before this: the name, the
 * address and the sign-out in a block at the foot of the sidebar, the role on a badge
 * in this corner, and Appearance in a nav group that also held the archive's
 * administration. Somebody looking for any of them now has one place to look, and the
 * sidebar is left saying only what the archive holds.
 *
 * A disclosure rather than an ARIA menu. It holds links and a button, Tab walks them
 * in order, and `role="menu"` would promise arrow-key semantics this does not
 * implement — a promise a screen reader passes on to its reader.
 *
 * Not portaled, unlike the pickers in components/ui: this is drawn inside .header,
 * which is sticky and stacked above the page, so a plain absolute box already paints
 * over the content it hangs across. The sign-out confirmation is a Modal, which
 * portals to <body> at --z-modal and so covers this in turn.
 */
function AccountMenu() {
  const shell = useContext(ShellCtx)
  const canBrowse = useCanBrowse()
  const [open, setOpen] = useState(false)
  const [asking, setAsking] = useState(false)
  const wrap = useRef(null)
  const button = useRef(null)

  /* The two ways out of an open menu that are not choosing something from it.
     Escape hands the focus back to the button it came from, since that is where the
     keyboard was; a click outside leaves the focus where the click put it. */
  useEffect(() => {
    if (!open) return
    const onKey = (e) => {
      if (e.key !== 'Escape') return
      // Before a dialog underneath can take it as its own dismissal.
      e.stopPropagation()
      setOpen(false)
      button.current?.focus()
    }
    /* pointerdown rather than click: pressing a control elsewhere on the page should
       shut this on the way down, not after that control has already acted. */
    const onDown = (e) => {
      if (!wrap.current?.contains(e.target)) setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onDown)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onDown)
    }
  }, [open])

  if (!shell) return null
  const { user } = shell
  const can = canSignOut(user)
  /* Filtered by the same rule the sidebar applies to its own items, so the menu can
     no more offer a page this account cannot open than the nav can. Today every entry
     in ACCOUNT_SETTINGS is `viewer: true` and nothing is filtered out — the rule is
     here for the next one, not for these. */
  const settings = ACCOUNT_SETTINGS.filter(
    (i) => (canBrowse || i.viewer) && (!i.cap || user.caps?.[i.cap])
  )

  return (
    <div className="acct" ref={wrap}>
      <button
        type="button"
        ref={button}
        className={`btn btn-sm btn-icon acct-btn${open ? ' active' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="account-menu"
        title={`${user.name} — your account`}
        aria-label={`Your account: ${user.name}, ${user.email}`}
      >
        {/* Drawn rather than typed, for the reason the menu button gives: 👤 is an
            emoji on one platform and a glyph on the next, and neither centres in a
            34px box the way the two circles of a person icon do. */}
        <svg width="17" height="17" viewBox="0 0 20 20" aria-hidden="true">
          <circle cx="10" cy="6.5" r="3.25" fill="none" stroke="currentColor" strokeWidth="1.6" />
          <path
            d="M3.75 17c0-3.2 2.8-5.25 6.25-5.25S16.25 13.8 16.25 17"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      </button>
      {/* Kept in the tree while shut, so `hidden` is what closes it: a menu built
          only on open would lose the focus ring on the button that opened it in
          browsers that treat the removal as a focus change. */}
      <div className="acct-menu" id="account-menu" hidden={!open}>
        <div className="acct-who">
          <div className="acct-name truncate" title={user.name}>
            {user.name}
          </div>
          <div className="acct-mail truncate" title={user.email}>
            {user.email}
          </div>
          <div className="acct-facts">
            <Badge tone={user.role === 'owner' ? 'accent' : undefined} title={user.roleLabel}>
              {/* A donor's role *is* viewer — that is the whole design — but the word
                  should be the one they would use about themselves. */}
              {user.isDonor ? 'donor' : user.role}
            </Badge>
            {/* Which door this session came through. Three answers, and the third is
                worth saying out loud: on a dev server there is no session at all,
                which is also why Sign out below is disabled. */}
            <span className="small subtle">
              {user.identity === 'dev'
                ? 'Local development identity'
                : user.isDonor
                  ? 'Signed in on the donor door'
                  : 'Signed in through Cloudflare Access'}
            </span>
          </div>
        </div>
        {settings.length > 0 && (
          <div className="acct-group">
            <div className="acct-group-label">Settings</div>
            {settings.map((i) => (
              <Link
                key={i.to}
                to={i.to}
                className="acct-item"
                onClick={() => setOpen(false)}
              >
                <span className="acct-item-icon" aria-hidden="true">
                  <Icon name={i.icon} size={15} />
                </span>
                <span className="truncate">{i.label}</span>
              </Link>
            ))}
          </div>
        )}
        {/* Its own group rather than an entry under Settings: it is not something you
            can set, and this menu is the only place in the application it can be reached
            from — there are no public pages to put it on. Above Sign out, which is the
            last thing anybody wants from a menu and stays last. */}
        <div className="acct-group">
          <Link to="/privacy" className="acct-item" onClick={() => setOpen(false)}>
            <span className="acct-item-icon" aria-hidden="true">
              <Icon name="shield" size={15} />
            </span>
            <span className="truncate">Privacy policy</span>
          </Link>
        </div>
        <div className="acct-group">
          <button
            type="button"
            className="acct-item acct-out"
            onClick={() => {
              setOpen(false)
              setAsking(true)
            }}
            disabled={!can}
            title={can ? 'End this session and leave the archive' : NO_SIGN_OUT_HINT}
          >
            <span className="acct-item-icon" aria-hidden="true">
              <Icon name="power" size={15} />
            </span>
            Sign out
          </button>
        </div>
      </div>
      {/* Behind a confirmation because the way back in is a one-time PIN fetched from
          an inbox, which is a long walk for a mis-click, and — for anyone who can
          write — because a half-written update would go with it. */}
      <Modal
        open={asking}
        onClose={() => setAsking(false)}
        title="Sign out?"
        size="sm"
        footer={
          <>
            <button className="btn btn-primary" onClick={() => signOut(user)}>
              Sign out
            </button>
            <button className="btn" onClick={() => setAsking(false)}>
              Stay signed in
            </button>
          </>
        }
      >
        <p>
          This ends {user.isDonor ? 'your' : 'the Cloudflare Access'} session for{' '}
          <strong>{user.email}</strong> in this browser. {SIGN_IN_AGAIN_HINT(user)}
        </p>
        {mayLoseUnsavedWork(user) && (
          <p style={{ marginTop: 10, color: 'var(--fg-muted)' }}>
            Anything you are part way through editing and have not saved will be lost.
          </p>
        )}
      </Modal>
    </div>
  )
}

/** An app's mark on its tile: the artwork over --brand, as .nav-logo draws this app's own. */
function SuiteTile({ app, size = 28 }) {
  return (
    <span
      className="suite-tile"
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        backgroundImage: `url('${app.mark}')`,
        backgroundSize: app.size,
      }}
    />
  )
}

/**
 * The way across to S4CA's other apps (lib/suite.js). The button shows the other app's
 * mark while there is only one to go to; with more, a grid, and the menu says which is
 * which. The menu lists every app, this one first-class among them but not a link, so
 * the list reads the same from whichever app it is opened in.
 *
 * A disclosure, as the account menu beside it is and for the same reasons, closed the
 * same two ways. The links leave the page rather than open a tab: flipping to the other
 * app is going there, and its own switcher is the way back.
 *
 * Not for a donor. The other apps are staff tools behind Access applications of their
 * own, which a donor's address is not on.
 */
function AppSwitcher() {
  const shell = useContext(ShellCtx)
  const [open, setOpen] = useState(false)
  const wrap = useRef(null)
  const button = useRef(null)

  useEffect(() => {
    if (!open) return
    const onKey = (e) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      setOpen(false)
      button.current?.focus()
    }
    const onDown = (e) => {
      if (!wrap.current?.contains(e.target)) setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onDown)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onDown)
    }
  }, [open])

  if (!shell || shell.user.isDonor) return null
  const others = SUITE.filter((a) => a.key !== HERE)
  if (!others.length) return null
  const only = others.length === 1 ? others[0] : null
  const label = only ? `Switch to ${only.name}` : 'Switch to another S4CA app'

  return (
    <div className="suite" ref={wrap}>
      <button
        type="button"
        ref={button}
        className={`btn btn-sm btn-icon suite-btn${open ? ' active' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="suite-menu"
        title={label}
        aria-label={label}
      >
        {only ? (
          <SuiteTile app={only} size={24} />
        ) : (
          <svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor" aria-hidden="true">
            {[2, 9, 16].flatMap((y) => [2, 9, 16].map((x) => <circle key={`${x}${y}`} cx={x} cy={y} r="1.9" />))}
          </svg>
        )}
      </button>
      <div className="acct-menu suite-menu" id="suite-menu" hidden={!open}>
        <div className="acct-group-label">S4CA apps</div>
        {SUITE.map((app) =>
          app.key === HERE ? (
            <div key={app.key} className="suite-item suite-here" aria-current="page">
              <SuiteTile app={app} />
              <span className="suite-text">
                <span className="suite-name truncate">{app.name}</span>
                <span className="suite-blurb">You are here</span>
              </span>
            </div>
          ) : (
            <a key={app.key} href={app.href} className="suite-item">
              <SuiteTile app={app} />
              <span className="suite-text">
                <span className="suite-name truncate">{app.name}</span>
                <span className="suite-blurb">{app.blurb}</span>
              </span>
            </a>
          )
        )}
      </div>
    </div>
  )
}

/**
 * Page chrome: title bar plus the page's actions, then the global controls.
 *
 * The page's buttons and the app's own two icons are separate elements inside one
 * row rather than one undifferentiated run, so a phone can deal them
 * onto two rows without the markup changing — the row itself goes
 * `display: contents` there and they become cells of the header's grid. On a
 * wide screen it is a plain flex row and they read left to right in this order.
 *
 * `back` is the one slot that sits left of the title, next to the menu button: a
 * way out of a record belongs where a reader looks for it, not among the actions
 * that operate on the record. Only rendered when a page passes one, and the class
 * goes with it because the narrow layout has to open a grid column to match.
 */
export function PageHeader({ title, sub, back, actions, children }) {
  /* The title and the line under it, in the installation's term. Not `children`: that
     is the page, and a page is where records are drawn. */
  const t = useRelabel()
  return (
    <>
      <div className={`header${back ? ' has-back' : ''}`}>
        <NavToggle />
        {back && <div className="header-back">{back}</div>}
        <div className="header-title">
          <h1>{t(title)}</h1>
        </div>
        <div className="header-controls">
          <div className="header-actions">{actions}</div>
          {/* The role badge that used to sit here is in the account menu below — it
              said the same thing on every page, and then the same thing again beside
              the dashboard greeting. The divider went with it. There is one again,
              drawn by the app switcher on its own left edge: it starts the app's
              controls, which is what a line between them and the page's actions says.

              The app's own controls, as against the page's: one cell on a phone, so
              they stay together at the end of the row rather than opening a fourth
              column on the narrowest screen. */}
          <div className="header-chrome">
            <AppSwitcher />
            <ThemeCycleButton />
            <AccountMenu />
          </div>
        </div>
      </div>
      {/* Under the bar rather than in it, as s4caFileMaker sets it: the bar holds the
          page's name alone, and the line saying what the page is starts the page. */}
      {sub && <div className="page-sub">{t(sub)}</div>}
      <div className="page">{children}</div>
    </>
  )
}

/**
 * Search used to be a page at /search. It is the top of the dashboard now, so a
 * bookmark or an old link lands there instead — carrying its query, since arriving
 * at an empty box would be the same as losing the link.
 */
function SearchRedirect() {
  const [params] = useSearchParams()
  const q = params.get('q')
  return <Navigate to={q ? `/?q=${encodeURIComponent(q)}` : '/'} replace />
}

/** Above this width the sidebar is a column of the grid; below it, a drawer. */
const WIDE = '(min-width: 1001px)'
const NAV_KEY = 's4n.nav.collapsed'

const readCollapsed = () => {
  try {
    return localStorage.getItem(NAV_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * Which door this browser is standing at, once it turns out nobody is signed in.
 *
 * Only asked after a 401, and only once — a signed-in page never asks at all. The
 * answer comes from the Worker rather than from `location.hostname` because the donor
 * hostname is configuration (`DONOR_HOST` in wrangler.jsonc), and a copy of it compiled
 * into this bundle would be a second place for it to be true.
 *
 * `undefined` means the question is still out, which is a third state and not the same
 * as "no": rendering the Zero Trust notice while waiting would show a donor the wrong
 * screen for a moment on every visit.
 */
function useDoor(unauthenticated) {
  // The context itself rather than just the verdict, because the donor sign-in page
  // needs what came with it — the Turnstile site key, which cannot be a build-time
  // constant: one bundle is served at both hostnames.
  const [ctx, setCtx] = useState(undefined)
  useEffect(() => {
    if (!unauthenticated) return
    let live = true
    donorContext().then((c) => live && setCtx(c ?? null))
    return () => {
      live = false
    }
  }, [unauthenticated])
  return {
    door: ctx === undefined ? undefined : ctx ? 'donor' : 'staff',
    donorCtx: ctx || null,
  }
}

export default function App() {
  const me = useMe()
  const lookups = useLookups()
  /* Up here with the other shell-wide queries and not beside the count it feeds, which
     is below several early returns — a hook that only runs for an active account is a
     hook that changes the order React sees. Cheap either way: a list of integers. */
  const favorites = useApi('favorites', 'favorites', { staleTime: 60_000 })
  /* The rest of the sidebar's badges. One small request for six counts, which is the same
     bargain the favourites count strikes: a badge that can move when its page's contents
     move, without refetching the account to find out. */
  const navTotals = useApi('nav-counts', 'nav-counts', { staleTime: 60_000 })
  /* The two shelves, for their badges. Same query keys the two pages use, so opening
     either is one request rather than two and shelving a report refreshes the badge
     without being told to — the import pages refetch that key already. Longer stale
     window than the rest: a shelf only changes when somebody imports a file, and both
     lists are a few dozen rows of metadata. */
  const annualReports = useApi('annual-reports', 'annual-reports', { staleTime: 5 * 60_000 })
  const newsletterIssues = useApi('newsletter-issues', 'newsletter-issues', { staleTime: 5 * 60_000 })
  const { door, donorCtx } = useDoor(me.isError && me.error?.status === 401)
  // Two states, because the sidebar means two different things by width: on a
  // wide screen it is always there until folded away (and that choice sticks),
  // on a narrow one it is a drawer that starts shut and closes as you navigate.
  const [navOpen, setNavOpen] = useState(false)
  const [navCollapsed, setNavCollapsed] = useState(readCollapsed)
  const [navCounts, setNavCounts] = useState({})
  const [wide, setWide] = useState(() => window.matchMedia(WIDE).matches)
  const location = useLocation()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const leaving = useLeaving()

  useEffect(() => setNavOpen(false), [location.pathname])

  // Signing out leaves the document, and the browser may park it whole — React tree,
  // query cache and all — to hand back on Back. Restored that way the archive would be
  // on screen again after being left, which is the one thing the sign-out button is
  // there to prevent. A restore re-asks for everything: if the session did end,
  // /api/me answers 401 and the sign-in notice takes the screen. (The same applies to
  // a tab woken after a week, where the answer is only stale rather than forbidden.)
  useEffect(() => {
    const onShow = (e) => {
      if (e.persisted) qc.invalidateQueries()
    }
    window.addEventListener('pageshow', onShow)
    return () => window.removeEventListener('pageshow', onShow)
  }, [qc])

  useEffect(() => {
    const mq = window.matchMedia(WIDE)
    const onChange = () => setWide(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  const navVisible = wide ? !navCollapsed : navOpen
  const toggleNav = () => {
    if (wide) {
      setNavCollapsed((v) => {
        try {
          localStorage.setItem(NAV_KEY, v ? '0' : '1')
        } catch {}
        return !v
      })
    } else {
      setNavOpen((v) => !v)
    }
  }

  // "/" focuses the global search from anywhere. The box is on the dashboard now:
  // if it is on screen the cursor just goes to it, and from anywhere else this is a
  // trip home, where the dashboard focuses it on arrival.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === '/' && !/^(INPUT|TEXTAREA)$/.test(e.target.tagName) && !e.target.isContentEditable) {
        e.preventDefault()
        const box = document.getElementById(SEARCH_INPUT_ID)
        if (box) box.focus()
        else navigate(OVERVIEW, { state: { focusSearch: true } })
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [navigate])

  /*
   * Before everything below, and before any error any of it could draw. Once the app has
   * committed to a navigation there is nothing on this document worth rendering except the
   * fact that it is leaving — see LeavingScreen, which exists because what used to fill
   * this gap was a red card nobody could read in the time it was up.
   */
  if (leaving.reason) return <LeavingScreen reason={leaving.reason} to={leaving.to} />

  if (me.isLoading) {
    return (
      <div className="page" style={{ maxWidth: 520, margin: '18vh auto' }}>
        <Loading label="Checking your access…" rows={2} />
      </div>
    )
  }

  if (me.isError) {
    const status = me.error?.status

    // Nobody is signed in and this is the donor hostname: its own sign-in, forgotten
    // password and set-password screens stand in for the whole application until one
    // of them succeeds. Held back until the question is answered rather than guessed
    // at — see useDoor.
    if (status === 401 && door === undefined) {
      return (
        <div className="page" style={{ maxWidth: 520, margin: '18vh auto' }}>
          <Loading label="Checking your access…" rows={2} />
        </div>
      )
    }
    if (status === 401 && door === 'donor') return <DonorSignIn ctx={donorCtx} />

    return (
      <div className="page" style={{ maxWidth: 620, margin: '12vh auto' }}>
        <div className="col">
          {/* The one screen with no account behind it, so the only one that cannot
              know what this installation calls itself. */}
          <h1>{FALLBACK_ORG}</h1>
          {me.error?.unreachable ? (
            <Notice tone="danger" icon="power" title="The API is not running">
              <p>{me.error.detail}</p>
              <pre
                style={{
                  background: 'var(--bg-sunken)',
                  padding: 12,
                  borderRadius: 8,
                  overflowX: 'auto',
                  margin: '10px 0',
                }}
              >
                npm run dev        # serves the API and the app together{'\n'}
                npm run dev:api    # API only, alongside `npm run dev:ui`
              </pre>
              <button className="btn btn-primary" onClick={() => me.refetch()}>
                Retry
              </button>
            </Notice>
          ) : status === 401 ? (
            <Notice tone="warn" icon="lock" title="You are not signed in">
              This application sits behind Cloudflare Zero Trust. Reload the page to be sent through
              the one-time PIN sign-in, or open it from the Access dashboard.
              <div style={{ marginTop: 10 }}>
                <button className="btn btn-primary" onClick={() => window.location.reload()}>
                  Reload and sign in
                </button>
              </div>
            </Notice>
          ) : (
            <ErrorState error={me.error} retry={me.refetch} />
          )}
        </div>
      </div>
    )
  }

  const user = me.data

  if (!user.isActive) {
    return (
      <div className="page" style={{ maxWidth: 620, margin: '12vh auto' }}>
        <div className="col">
          <h1>{user.orgName || FALLBACK_ORG}</h1>
          {/* Two different situations behind one flag. Somebody new is waiting for an owner;
              somebody an owner switched off is not, and telling them to wait would be
              untrue. The Worker decides which, from the audit trail (AWAITING_APPROVAL_SQL). */}
          {user.awaitingApproval ? (
            <Notice tone="warn" icon="pending" title="Your account is awaiting approval">
              You signed in as <strong>{user.email}</strong>, but no access level has been granted yet.
              An owner needs to activate your account on the Users &amp; access page.
            </Notice>
          ) : (
            <Notice tone="warn" icon="lock" title="This account has been switched off">
              You signed in as <strong>{user.email}</strong>, but an owner has switched this account off,
              so it cannot open anything here. If you think that is a mistake, ask an owner.
            </Notice>
          )}
          <div className="row">
            <ThemeCycleButton />
            <button className="btn" onClick={() => me.refetch()}>
              Check again
            </button>
            {/* This screen is outside the shell, so the sidebar's sign-out is not on
                it — and it is the screen that needs one most. Someone who signed in
                with the wrong address of two is otherwise stuck here, waiting for the
                approval of an account nobody means to approve. No confirmation: there
                is nothing on this page to lose. */}
            <button
              className="btn"
              onClick={() => signOut(user)}
              disabled={!canSignOut(user)}
              title={canSignOut(user) ? 'Sign out and try another address' : NO_SIGN_OUT_HINT}
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    )
  }

  /*
   * Every badge follows its list page: alumni are left out unless a page asks otherwise,
   * and an open list page overrides the default with its own filtered total.
   *
   * THE FALLBACK HAS TO COUNT WHAT THE PAGE COUNTS, and it twice did not. It was reading
   * `status === 'active'` where every one of those pages — and the dashboard it claimed to
   * agree with — leaves out alumni and keeps the rest; and it counted the whole registry
   * where the Partner Units page has shown staff units alone since migration 0031. Both
   * showed the same way: a badge reading one number until you opened the page, and another
   * once you had. It has to be derived here rather than fetched, because these come off the
   * lookups payload the sidebar already has, so the rule is simply to write the page's own
   * filter out three times.
   */
  const allPartners = lookups.data?.partners
  const notAlumni = allPartners?.filter((p) => p.status !== 'alumni')
  const currentOfKind = (kind) => countOfKind(allPartners, kind)
  const counts = {
    newsletters:
      navCounts.newsletters !== undefined
        ? { value: navCounts.newsletters, title: 'Updates matching the filters on the Updates page' }
        : {
            value: notAlumni?.reduce((a, p) => a + (p.newsletter_count || 0), 0),
            title: 'Updates from current partner units — alumni left out, as on the Updates page',
          },
    partners:
      navCounts.partners !== undefined
        ? { value: navCounts.partners, title: 'Partner units matching the filters on the Partner Units page' }
        : { value: currentOfKind('staff'), title: 'Current partner units — alumni left out, as on the page' },
    /* The other two Register pages, each counting its own kind. Without these the two
       badges were simply absent: `useNavCount` sets them once their page has been opened,
       and nothing filled them in before that. */
    trainingSchools:
      navCounts.trainingSchools !== undefined
        ? {
            value: navCounts.trainingSchools,
            title: 'Training schools matching the filters on the Training Schools page',
          }
        : { value: currentOfKind('training'), title: 'Current training schools — alumni left out, as on the page' },
    projects:
      navCounts.projects !== undefined
        ? { value: navCounts.projects, title: 'Projects matching the filters on the Projects page' }
        : { value: currentOfKind('project'), title: 'Current projects — alumni left out, as on the page' },
    // Always the whole shortlist, never what the Favorites page is currently filtered
    // down to — the badge answers "how many have I picked", which a search typed on that
    // page does not change. Its own small request rather than a field on /api/me, so
    // pressing a heart can refresh it without refetching the account.
    favorites: {
      value: favorites.data?.partner_ids?.length,
      title: 'Partner units you follow',
    },
    // The two shelves have no filters and no paging, so their badge is the length of
    // the list the page draws and there is nothing for a page to override. It counts
    // every row on the shelf, including — for an account served the covered edition —
    // a document that has no such edition and is listed as present but unavailable.
    // That is what the page shows, and a badge that quietly counted something else
    // would be the one number in this sidebar that disagrees with its own page.
    annualReports: {
      value: annualReports.data?.rows?.length,
      title: 'Annual reports on the shelf',
    },
    newsletterIssues: {
      value: newsletterIssues.data?.rows?.length,
      title: 'Printed newsletters on the shelf',
    },
    /* The rest, straight off /api/nav-counts. Each is what its own page shows — the two
       update counts leave alumni out because those pages do — and each is absent rather
       than zero for an account whose view does not include the page, which is what keeps
       a badge from appearing beside an entry that is not there. */
    prayerRequests: {
      value: navTotals.data?.prayer_requests,
      title: 'Updates carrying a prayer request — alumni left out, as on the page',
    },
    highlighted: {
      value: navTotals.data?.highlighted,
      title: 'Updates the archive has starred — alumni left out, as on the page',
    },
    media: { value: navTotals.data?.media, title: 'Pictures in the media library' },
    countries: { value: navTotals.data?.countries, title: 'Countries and categories' },
    users: { value: navTotals.data?.users, title: 'People who may sign in at the staff door' },
    donors: { value: navTotals.data?.donors, title: 'Donor accounts at the supporters’ door' },
    /* Everybody the giving report knows about, which is a much longer list than the
       accounts above: the register does not need an account to hold a donor. Absent
       rather than zero for a role that may not open it, so no badge appears beside a
       link that is not there either. */
    donorRegistry: {
      value: navTotals.data?.donorRegistry,
      title: 'Donors in the giving register',
    },
    communications: {
      value: navTotals.data?.communications,
      title: 'Letters the archive has sent to partner units',
    },
  }

  return (
    <NavCountCtx.Provider value={setNavCounts}>
    {/* What this installation calls a partner unit, from the same request that
        authenticated the page — so the first paint already has the right word. */}
    <PartnerTermProvider term={user.partnerTerm}>
    <ViewModeProvider canBrowse={user.caps.browse} anonVersion={user.anonVersion}>
    <ShellCtx.Provider value={{ user, navVisible, toggleNav }}>
    <div className={`shell${navCollapsed ? ' nav-collapsed' : ''}`}>
      <IdleWatch user={user} />
      {navOpen && <div className="nav-scrim" onClick={() => setNavOpen(false)} />}
      <Nav
        open={navOpen}
        onClose={() => setNavOpen(false)}
        caps={user.caps}
        counts={counts}
        orgName={user.orgName || FALLBACK_ORG}
      />
      <div className="main">
        {/* Keyed on the path so navigating away from a page that threw resets the
            boundary — otherwise the first broken render replaces every page after
            it with the same apology. */}
        <ErrorBoundary key={location.pathname}>
        {!user.caps.browse ? (
          <ViewerRoutes user={user} />
        ) : (
        <Routes>
          <Route path="/dashboard" element={<Dashboard user={user} />} />
          <Route path={OVERVIEW} element={<UpdatesOverview user={user} />} />
          <Route path="/" element={<Home />} />
          <Route path="/search" element={<SearchRedirect />} />
          <Route path="/annual-reports" element={<AnnualReports />} />
          <Route path="/newsletter-issues" element={<NewsletterIssues />} />
          {/* The directory with one filter it cannot clear — the same component, so the
              two pages cannot drift apart. See PartnerList. */}
          <Route path="/favorites" element={<PartnerList user={user} onlyFavorites />} />

          <Route path="/newsletters" element={<NewsletterList user={user} />} />
          <Route path="/newsletters/new" element={<NewsletterForm user={user} mode="create" />} />
          <Route path="/newsletters/:id" element={<NewsletterDetail user={user} />} />
          <Route path="/newsletters/:id/edit" element={<NewsletterForm user={user} mode="edit" />} />
          <Route path="/prayer-requests" element={<PrayerRequests />} />
          <Route path="/prayer-needs" element={<PrayerRequests recent />} />
          <Route path="/highlighted-needs" element={<HighlightedNeeds user={user} />} />
          <Route path="/media" element={<MediaLibrary user={user} />} />

          <Route path="/partners" element={<PartnerList user={user} fundType="staff" />} />
          <Route path="/training-schools" element={<PartnerList user={user} fundType="training" />} />
          <Route path="/projects" element={<PartnerList user={user} fundType="project" />} />
          <Route path="/partners/new" element={<PartnerForm user={user} mode="create" />} />
          <Route path="/partners/:id" element={<PartnerDetail user={user} />} />
          <Route path="/partners/:id/edit" element={<PartnerForm user={user} mode="edit" />} />
          <Route path="/countries" element={<CountryList user={user} />} />
          <Route path="/donors" element={<Donors user={user} />} />
          <Route path="/communication/email" element={<EmailPartnerUnits user={user} />} />
          <Route path="/communication/templates" element={<EmailTemplates user={user} />} />
          <Route path="/communication/log" element={<CommunicationLog user={user} />} />
          <Route path="/countries/:id" element={<CountryDetail />} />

          <Route path="/reports" element={<ReportsIndex user={user} />} />
          <Route path="/reports/newsletters-by-partner" element={<NewslettersByPartner user={user} />} />
          <Route path="/reports/donors-by-partner" element={<DonorsByPartner user={user} />} />
          <Route path="/reports/coverage" element={<CoverageReport />} />
          <Route path="/reports/activity" element={<ActivityReport />} />
          <Route path="/reports/timeline" element={<TimelineReport />} />
          <Route path="/reports/countries" element={<CountryComparison />} />
          <Route path="/reports/text" element={<TextAnalytics />} />
          <Route path="/reports/ai-usage" element={<AiUsageReport />} />
          <Route path="/data-quality" element={<DataQuality />} />

          <Route path="/ai/prompts" element={<PromptsManager user={user} />} />
          <Route path="/ai/studio" element={<AiStudio user={user} />} />

          <Route path="/settings/appearance" element={<SettingsAppearance />} />
          <Route path="/settings/llm" element={<SettingsLlm user={user} />} />
          <Route path="/settings/users" element={<SettingsUsers user={user} />} />
          <Route path="/settings/donors" element={<SettingsDonors user={user} />} />
          <Route path="/settings/photo-import" element={<SettingsPhotoImport user={user} />} />
          <Route path="/settings/annual-reports" element={<SettingsAnnualReports user={user} />} />
          <Route path="/settings/newsletter-issues" element={<SettingsNewsletterIssues user={user} />} />
          <Route path="/settings/partner-import" element={<SettingsPartnerImport user={user} />} />
          <Route path="/settings/donor-import" element={<SettingsDonorImport user={user} />} />
          <Route path="/settings/audit" element={<AuditLog />} />
          <Route path="/settings/errors" element={<ErrorLog user={user} />} />

          {/* Not a setting and not part of any section, which is why it is reached from
              the account menu rather than the sidebar. Both routers carry it: it is the
              one page every account has the same right to read. */}
          <Route path="/privacy" element={<Privacy />} />

          <Route
            path="*"
            element={
              <PageHeader title="Page not found">
                <Notice tone="warn" icon="info" title="That page does not exist">
                  <Link to={TOP.to}>Go back to Home</Link>
                </Notice>
              </PageHeader>
            }
          />
        </Routes>
        )}
        </ErrorBoundary>
      </div>
    </div>
    </ShellCtx.Provider>
    </ViewModeProvider>
    </PartnerTermProvider>
    </NavCountCtx.Provider>
  )
}

/**
 * The whole application, for an account that is given a view of it rather than the run
 * of it: the pages of migration 0012, plus the two kinds of record those pages open —
 * a single update, and a partner unit.
 *
 * A router of its own rather than guards sprinkled through the full one. The pages that
 * are not here would have their data refused by the Worker anyway, so what this saves is
 * not access — it is the experience of following a link into an error. Anything else
 * typed into the address bar lands on the notice below, which says the page exists and
 * is not part of this view rather than pretending it was never there.
 */
function ViewerRoutes({ user }) {
  return (
    <Routes>
      <Route path="/dashboard" element={<Dashboard user={user} />} />
      <Route path={OVERVIEW} element={<UpdatesOverview user={user} />} />
      <Route path="/" element={<Home />} />
      <Route path="/search" element={<SearchRedirect />} />
      {/* The two pages outside migration 0012's list that such an account is given. The
          Worker serves both the face-covered edition of every file, and refuses rather
          than falling back when one has not got it. */}
      <Route path="/annual-reports" element={<AnnualReports />} />
      {/* And the other shelf, on the same terms. */}
      <Route path="/newsletter-issues" element={<NewsletterIssues />} />
      {/* The page this feature was built for. A donor holds no write capability and
          needs none: a favourite is a fact about the reader. See favorites() in the
          Worker, which is the only write in the API behind no capability at all. */}
      <Route path="/favorites" element={<PartnerList user={user} onlyFavorites />} />
      <Route path="/newsletters" element={<NewsletterList user={user} />} />
      <Route path="/newsletters/:id" element={<NewsletterDetail user={user} />} />
      <Route path="/prayer-requests" element={<PrayerRequests />} />
      <Route path="/prayer-needs" element={<PrayerRequests recent />} />
      {/* The directory and the record behind it. One without the other would be a
          wall of cards that each refuse to open — every card is a link to its unit. */}
      <Route path="/partners" element={<PartnerList user={user} fundType="staff" />} />
      {/* The same two entries the sidebar gives this account. They are `viewer: true` in
          NAV, and a sidebar entry whose route is not in this block lands on "not part of
          your view" — the one way these two lists can disagree. */}
      <Route path="/training-schools" element={<PartnerList user={user} fundType="training" />} />
      <Route path="/projects" element={<PartnerList user={user} fundType="project" />} />
      <Route path="/partners/:id" element={<PartnerDetail user={user} />} />
      <Route path="/highlighted-needs" element={<HighlightedNeeds user={user} />} />
      <Route path="/settings/appearance" element={<SettingsAppearance />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route
        path="*"
        element={
          <PageHeader title="Not part of your view">
            <Notice tone="info" icon="lock" title="This page is not one of yours">
              Your access covers Home, the annual reports, the newsletters,
              your favorites, the updates, the prayer requests, the highlighted needs, the partner
              units, the training schools, the projects, and the appearance of the app.
              The sidebar lists them all, and the privacy policy is in the account menu.
              <Link to={TOP.to}>Go back to Home</Link>.
            </Notice>
          </PageHeader>
        }
      />
    </Routes>
  )
}
